import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@store/auth/auth";
import { trackGaEvent } from "@utils/analytics";
import { queryClient } from "@/queryClient";
import {
  activateCustomer,
  createCustomer,
  createCustomerWithPermissions,
  deleteCustomerWithPermissions,
  removeCustomer,
  resetCustomerPassword,
  updateAppleConnectionRequest,
  updateCustomerCultureId,
  updateCustomerDetails,
  updateFacebookConnectionRequest,
} from "./services";
import type {
  ICreateCustomerRequest,
  IUpdateFacebookConnectionRequest,
} from "./types";

export const useUpdateCustomerDetailsMutation = () =>
  useMutation({
    mutationFn: updateCustomerDetails,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["customer", "metadata"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["customer", "details"],
        }),
      ]);
    },
  });

export const useUpdateFacebookConnectionMutation = () =>
  useMutation<void, Error, IUpdateFacebookConnectionRequest>({
    mutationFn: updateFacebookConnectionRequest,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["customer"] });
    },
  });

export const useUpdateAppleConnectionMutation = () =>
  useMutation({
    mutationFn: updateAppleConnectionRequest,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["customer"] });
    },
  });

export const useCreateCustomerMutation = () => {
  const { setToken } = useAuthStore();
  const navigate = useNavigate();

  return useMutation<string, Error, ICreateCustomerRequest>({
    mutationFn: createCustomer,
    onSuccess: (token) => {
      trackGaEvent("signup_completed_event");

      setToken(token);
      navigate("/");
    },
  });
};

export const useRemoveCustomerMutation = () => {
  return useMutation({
    mutationFn: removeCustomer,
  });
};

export const useCreateCustomerWithPermissionsMutation = () => {
  return useMutation({
    mutationFn: createCustomerWithPermissions,
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["jointBillServiceSettings", variables.jointBillServiceId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["overview"],
        }),
      ]);
    },
  });
};

export const useDeleteCustomerWithPermissionsMutation = () =>
  useMutation({
    mutationFn: deleteCustomerWithPermissions,
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["jointBillServiceSettings", variables.jointBillServiceId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["overview"],
        }),
      ]);
    },
  });

export const useResetCustomerPasswordMutation = (encryptedToken: string) =>
  useMutation({
    mutationFn: (newPassword: string) =>
      resetCustomerPassword({ newPassword, encryptedToken }),
  });

export const useUpdateCustomerCultureIdMutation = () =>
  useMutation({
    mutationFn: (cultureId: string) => updateCustomerCultureId(cultureId),
  });

export const useActivateCustomerMutation = () =>
  useMutation({
    mutationFn: activateCustomer,
    onSuccess: () =>
      void queryClient.invalidateQueries({
        queryKey: ["customer", "metadata"],
      }),
  });
